import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  updateProcessStep,
  UpdateProcessStepResponseDto,
} from "#modules/processSteps/infra/updateProcessStep.api";
import { productsQuery } from "#modules/products/infra/products.query";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { quoteRequestProcessStepsQuery } from "#modules/quoteRequests/infra/quoteRequestProcessSteps.query";
import { quoteRequestTransportQuery } from "#modules/quoteRequests/infra/quoteRequestTransport.query";
import { quoteRequestAtom } from "#shared/view/components/AppShell/Atoms";

export const useUpdateProcessStep = (options?: {
  onSuccess?: (updateProcessStep: UpdateProcessStepResponseDto) => void;
}) => {
  const queryClient = useQueryClient();

  const quoteRequest = useAtomValue(quoteRequestAtom);

  const { mutate, ...rest } = useMutation({
    mutationFn: updateProcessStep,
    onSuccess: (processStep) => {
      if (!processStep) {
        return;
      }
      void queryClient.invalidateQueries(
        quoteRequestLineQuery({
          id: processStep.quoteRequestLineId || undefined,
        }),
      );

      void queryClient.invalidateQueries(quoteRequestTransportQuery(quoteRequest?.id));

      if (processStep.quoteRequestId) {
        void queryClient.invalidateQueries(
          quoteRequestProcessStepsQuery(processStep.quoteRequestId),
        );
      }
      void queryClient.invalidateQueries(productsQuery());
      options?.onSuccess?.(processStep);
    },
    onError: (err) => {
      showErrorToast({ title: "Une erreur est survenue" });
      errorHandler(err);
    },
  });

  return { updateProcessStep: mutate, ...rest };
};
